import { useEffect, useRef } from "react";
import type { KeyboardEvent, MouseEvent, PointerEvent } from "react";
import type { PadId } from "../model/types";

const PADS: Array<{ id: PadId; index: number; name: string; detail: string; mode: "gate" | "shot" | "panic" }> = [
  { id: "syncKick", index: 1, name: "SYNC KICK", detail: "phase impulse", mode: "shot" },
  { id: "burstKill", index: 2, name: "BURST KILL", detail: "hold: chroma off", mode: "gate" },
  { id: "lineCrush", index: 3, name: "LINE CRUSH", detail: "collapse band", mode: "shot" },
  { id: "frameTear", index: 4, name: "FRAME TEAR", detail: "split frame", mode: "shot" },
  { id: "whiteout", index: 5, name: "WHITEOUT", detail: "hold: level high", mode: "gate" },
  { id: "freeze", index: 6, name: "FREEZE", detail: "hold frame", mode: "gate" },
  { id: "unlock", index: 7, name: "UNLOCK", detail: "hold: free drift", mode: "gate" },
  { id: "panic", index: 8, name: "PANIC", detail: "signal safe", mode: "panic" },
];

export function PerformancePads({ active, onPad }: {
  active: Partial<Record<PadId, boolean>>;
  onPad: (pad: PadId, pressed: boolean, mode: "gate" | "shot" | "panic") => void;
}) {
  const pointers = useRef(new Map<PadId, Set<number>>());
  const keyboardHeld = useRef(new Set<PadId>());
  const suppressedClicks = useRef(new Set<PadId>());
  const onPadRef = useRef(onPad);
  onPadRef.current = onPad;

  useEffect(() => () => {
    for (const pad of PADS) {
      if (pad.mode !== "gate") continue;
      if ((pointers.current.get(pad.id)?.size ?? 0) > 0 || keyboardHeld.current.has(pad.id)) {
        onPadRef.current(pad.id, false, pad.mode);
      }
    }
    pointers.current.clear();
    keyboardHeld.current.clear();
  }, []);

  const gateIsHeld = (id: PadId) => (pointers.current.get(id)?.size ?? 0) > 0 || keyboardHeld.current.has(id);

  const pointerDown = (event: PointerEvent<HTMLButtonElement>, pad: (typeof PADS)[number]) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const wasInactive = !gateIsHeld(pad.id);
    const owners = pointers.current.get(pad.id) ?? new Set<number>();
    owners.add(event.pointerId);
    pointers.current.set(pad.id, owners);
    if (pad.mode !== "gate" || wasInactive) onPad(pad.id, true, pad.mode);
  };

  const pointerUp = (event: PointerEvent<HTMLButtonElement>, pad: (typeof PADS)[number]) => {
    const owners = pointers.current.get(pad.id);
    if (!owners?.delete(event.pointerId)) return;
    if (owners.size === 0) {
      pointers.current.delete(pad.id);
      if (pad.mode === "gate" && !gateIsHeld(pad.id)) onPad(pad.id, false, pad.mode);
    }
  };

  const keyDown = (event: KeyboardEvent<HTMLButtonElement>, pad: (typeof PADS)[number]) => {
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    if (event.repeat || keyboardHeld.current.has(pad.id)) return;
    const wasInactive = !gateIsHeld(pad.id);
    keyboardHeld.current.add(pad.id);
    if (pad.mode !== "gate" || wasInactive) onPad(pad.id, true, pad.mode);
  };

  const keyUp = (event: KeyboardEvent<HTMLButtonElement>, pad: (typeof PADS)[number]) => {
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    if (!keyboardHeld.current.delete(pad.id)) return;
    if (pad.mode === "gate" && !gateIsHeld(pad.id)) onPad(pad.id, false, pad.mode);
    suppressedClicks.current.add(pad.id);
    window.setTimeout(() => suppressedClicks.current.delete(pad.id), 0);
  };

  const blur = (pad: (typeof PADS)[number]) => {
    if (keyboardHeld.current.delete(pad.id) && pad.mode === "gate" && !gateIsHeld(pad.id)) onPad(pad.id, false, pad.mode);
  };

  const activate = (event: MouseEvent<HTMLButtonElement>, pad: (typeof PADS)[number]) => {
    // Pointer and keyboard paths already preserve press/hold timing. A
    // zero-detail click is the activation path used by assistive technology.
    if (event.detail > 0 || suppressedClicks.current.delete(pad.id)) return;
    if (pad.mode === "gate") onPad(pad.id, !Boolean(active[pad.id]), pad.mode);
    else onPad(pad.id, true, pad.mode);
  };

  return (
    <section className="performance-pads" aria-label="Performance glitch controls">
      {PADS.map((pad) => (
        <button
          type="button"
          className={`performance-pad performance-pad--${pad.mode}`}
          key={pad.id}
          aria-label={`${pad.name}. ${pad.detail}.${pad.mode === "panic" ? " Latches until Resume Engine." : pad.mode === "gate" ? " Press and hold; assistive activation toggles." : " One shot."}`}
          aria-pressed={pad.mode === "shot" ? undefined : Boolean(active[pad.id])}
          onPointerDown={(event) => pointerDown(event, pad)}
          onPointerUp={(event) => pointerUp(event, pad)}
          onPointerCancel={(event) => pointerUp(event, pad)}
          onLostPointerCapture={(event) => pointerUp(event, pad)}
          onKeyDown={(event) => keyDown(event, pad)}
          onKeyUp={(event) => keyUp(event, pad)}
          onBlur={() => blur(pad)}
          onClick={(event) => activate(event, pad)}
        >
          <span className="performance-pad__number">0{pad.index}</span>
          <strong>{pad.name}</strong>
          <small>{pad.detail}</small>
        </button>
      ))}
    </section>
  );
}
